import { trackEvent } from '../analytics/events';

const STATUS_KEY = 'gc_auth_status';
const EMAIL_KEY = 'gc_auth_email';
const ITEM_ATTR = 'data-gc-nav-item';
const BASE_LINKS = [
  { href: '/home.html', label: 'For Parents' },
  { href: '/educator.html', label: 'For Educators' },
  { href: '/product.html', label: 'Product' },
  { href: '/pricing.html', label: 'Pricing' },
];

function getPreviewState() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('navAuthPreview');
  if (
    !['in', 'out'].includes(status) ||
    !['127.0.0.1', 'localhost'].includes(window.location.hostname)
  )
    return null;
  return { status, email: params.get('navAuthEmail') || 'Account' };
}

function getAuthState() {
  const preview = getPreviewState();
  if (preview) return preview;
  return {
    status: localStorage.getItem(STATUS_KEY) || 'out',
    email: localStorage.getItem(EMAIL_KEY) || 'Account',
  };
}

function findNavActions() {
  const nav = document.querySelector('.marketing-nav');
  if (!nav) return null;
  return (
    nav.querySelector('.nav-actions') ||
    nav.querySelector('.brand-wordmark, .brand-logo, .brand-logo-nav')
      ?.nextElementSibling ||
    null
  );
}

function ensureMobileShell(container) {
  const nav = container.closest('.marketing-nav');
  if (!nav) return {};
  let mobileMeta = nav.querySelector('.nav-mobile-meta');
  if (!mobileMeta) {
    mobileMeta = document.createElement('div');
    mobileMeta.className = 'nav-mobile-meta';
    nav.appendChild(mobileMeta);
  }
  let badge = mobileMeta.querySelector('.auth-status-badge');
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'auth-status-badge';
    badge.setAttribute(ITEM_ATTR, 'auth-badge');
    mobileMeta.appendChild(badge);
  }
  let button = mobileMeta.querySelector('.hamburger-btn');
  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'hamburger-btn';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', 'Open menu');
    button.textContent = '☰';
    mobileMeta.appendChild(button);
  }
  let sheet = nav.querySelector('.nav-sheet');
  if (!sheet) {
    sheet = document.createElement('div');
    sheet.className = 'nav-sheet';
    nav.appendChild(sheet);
  }
  return { badge, button, nav, sheet };
}

function removeItems(container, items) {
  items.forEach((item) =>
    container.querySelector(`[${ITEM_ATTR}="${item}"]`)?.remove(),
  );
}

function createItem(tag, item, text, className, href = '#') {
  const node = document.createElement(tag);
  node.setAttribute(ITEM_ATTR, item);
  node.className = className;
  node.textContent = text;
  if (tag === 'a') node.href = href;
  return node;
}

function resolveHref(href) {
  return href;
}

function ensureGetStarted(container) {
  if (
    container.querySelector(`[${ITEM_ATTR}="get-started"]`) ||
    container.querySelector('a.btn.btn-primary, a.btn')
  )
    return;
  container.appendChild(
    createItem(
      'a',
      'get-started',
      'Get Started',
      'btn btn-primary',
      '/app.html',
    ),
  );
}

function createSheetLink(item, text, href, className = 'nav-link') {
  return createItem('a', item, text, className, resolveHref(href));
}

function renderSheet(container, isLoggedIn) {
  const { sheet } = ensureMobileShell(container);
  if (!sheet) return;
  sheet.replaceChildren();
  BASE_LINKS.forEach((link) => {
    sheet.appendChild(
      createSheetLink(
        link.label.toLowerCase().replace(/\s+/g, '-'),
        link.label,
        link.href,
      ),
    );
  });
  if (isLoggedIn) {
    sheet.append(
      createSheetLink(
        'dashboard',
        'Dashboard',
        '/app.html?screen=dashboard',
        'btn btn-secondary',
      ),
      createSheetLink('logout', 'Log out', '#'),
    );
    return;
  }
  sheet.append(
    createSheetLink('signin', 'Sign in', '#'),
    createSheetLink(
      'get-started',
      'Get Started',
      '/app.html',
      'btn btn-primary',
    ),
  );
}

function renderLoggedOut(container) {
  removeItems(container, ['account', 'dashboard', 'logout']);
  ensureGetStarted(container);
  const { badge } = ensureMobileShell(container);
  if (badge) {
    badge.textContent = '';
    badge.hidden = true;
  }
  if (container.querySelector(`[${ITEM_ATTR}="signin"]`)) return;
  const cta = container.querySelector('a.btn.btn-primary, a.btn');
  const signIn = createItem('a', 'signin', 'Sign in', 'nav-link');
  if (cta) container.insertBefore(signIn, cta);
  else container.appendChild(signIn);
  renderSheet(container, false);
}

function renderLoggedIn(container) {
  removeItems(container, ['signin']);
  const cta = container.querySelector('a.btn.btn-primary, a.btn');
  const { email } = getAuthState();
  const account = createItem(
    'span',
    'account',
    `\uD83D\uDC64 ${email}`,
    'nav-link',
  );
  account.title = email;
  account.style.cssText =
    'opacity:.75;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
  const dashboard = createItem(
    'a',
    'dashboard',
    'Dashboard',
    'btn btn-secondary',
    '/app.html?screen=dashboard',
  );
  const logout = createItem('a', 'logout', 'Log out', 'nav-link');
  const { badge } = ensureMobileShell(container);
  if (badge) {
    badge.textContent = `👤 ${email}`;
    badge.title = email;
    badge.hidden = false;
  }
  cta?.remove();
  container.querySelector(`[${ITEM_ATTR}="get-started"]`)?.remove();
  removeItems(container, ['account', 'dashboard', 'logout']);
  container.append(account, dashboard, logout);
  renderSheet(container, true);
}

function render(container) {
  if (!container) return;
  if (getAuthState().status === 'in') renderLoggedIn(container);
  else renderLoggedOut(container);
  container.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href') || '';
    if (href.startsWith('/')) link.setAttribute('href', resolveHref(href));
  });
}

function closeSheet(container) {
  const { button, sheet } = ensureMobileShell(container);
  button?.setAttribute('aria-expanded', 'false');
  button?.setAttribute('aria-label', 'Open menu');
  if (button) button.textContent = '☰';
  sheet?.classList.remove('open');
}

function toggleSheet(container) {
  const { button, sheet } = ensureMobileShell(container);
  if (!button || !sheet) return;
  const nextOpen = !sheet.classList.contains('open');
  sheet.classList.toggle('open', nextOpen);
  button.setAttribute('aria-expanded', String(nextOpen));
  button.setAttribute('aria-label', nextOpen ? 'Close menu' : 'Open menu');
  button.textContent = nextOpen ? '✕' : '☰';
}

async function openLoginModal() {
  void trackEvent('login_opened', { app_surface: 'site' });
  const [{ showLoginModal }] = await Promise.all([
    import('../components/LoginModal.js'),
    import('./authService.ts'),
  ]);
  showLoginModal();
}

async function logoutUser() {
  const { signOutUser } = await import('./authService.ts');
  await signOutUser();
  localStorage.setItem(STATUS_KEY, 'out');
  localStorage.removeItem(EMAIL_KEY);
}

async function completeGoogleRedirectIfPresent() {
  const { completeGoogleRedirectSignIn, dispatchAuthSuccess } = await import(
    './authService.ts'
  );
  const user = await completeGoogleRedirectSignIn();
  if (!user) return;
  localStorage.setItem(STATUS_KEY, 'in');
  localStorage.setItem(EMAIL_KEY, user.email || 'Account');
  dispatchAuthSuccess(user, 'google_redirect');
  document.getElementById('login-modal')?.remove();
}

function attachHandlers(container) {
  const { nav, button, sheet } = ensureMobileShell(container);
  button?.addEventListener('click', () => toggleSheet(container));
  container.addEventListener('click', async (event) => {
    const target = event.target.closest(`[${ITEM_ATTR}]`);
    if (!target) return;
    const item = target.getAttribute(ITEM_ATTR);
    if (item === 'signin') {
      event.preventDefault();
      await openLoginModal();
    }
    if (item === 'logout') {
      event.preventDefault();
      await logoutUser();
      render(container);
    }
  });
  sheet?.addEventListener('click', async (event) => {
    const target = event.target.closest('a, button');
    if (!target) return;
    const item = target.getAttribute(ITEM_ATTR);
    if (item === 'signin') {
      event.preventDefault();
      closeSheet(container);
      await openLoginModal();
      return;
    }
    if (item === 'logout') {
      event.preventDefault();
      closeSheet(container);
      await logoutUser();
      render(container);
      return;
    }
    closeSheet(container);
  });
  document.addEventListener('click', (event) => {
    if (!nav?.contains(event.target)) closeSheet(container);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeSheet(container);
  });
}

const navActions = findNavActions();
if (navActions) {
  window.addEventListener('auth-success', (event) => {
    const email =
      event.detail?.user?.email || localStorage.getItem(EMAIL_KEY) || 'Account';
    localStorage.setItem(STATUS_KEY, 'in');
    localStorage.setItem(EMAIL_KEY, email);
    render(navActions);
  });
  window.addEventListener('storage', (event) => {
    if (event.key === STATUS_KEY || event.key === EMAIL_KEY) render(navActions);
  });
  attachHandlers(navActions);
  void completeGoogleRedirectIfPresent()
    .catch((error) => {
      console.error('Google redirect sign-in failed:', error);
    })
    .finally(() => {
      render(navActions);
    });
}
